// AGENT-SPEC v2 (consolidate-then-demo §2.1, A3/A7): the ONE spec format. A `companyos.agent-spec/v2`
// document is the whole desired setup of one Hermes PROFILE on a container, in the shape
// ClawPanel's applier writes it: model (and its auxiliary models), toolsets, MCP servers and
// include lists, skills with every shipped file's hash, plugins with their files and hashes,
// approvals, memory, key references, the chattr +i paths, and SOUL.md / AGENTS.md content.
//
//   GET  /v1/agent-spec/{container}                     the container's signed v2 bundle (its token)
//   GET  /v1/agent-spec/{container}/{profile}           one profile's v2 document (platform admins)
//   POST /v1/agent-spec/{container}/{profile}/applied   the applier's report (platform admins)
//
// Rule for every part: null means NOT MANAGED — the applier leaves that part as the container has
// it. Inside a managed part, a field CompanyOS has no source for yet is the literal "unknown"
// (never guessed) and its dotted path is in `unknown_fields`.
//
// Signed exactly like v1 (`companyos.agent-spec-bundle/v1`): the payload is the canonical JSON
// (sorted keys, no whitespace) of an `AgentSpecV2Bundle`, signed Ed25519 with the current key of
// `GET /v1/agent-spec-keys`; the envelope carries it base64url. Verify the bytes, then parse.
//
// Secrets: references only (where a credential is used, its env name), never a value. Every
// string is scanned for secret-shaped text and the schema refuses a hit; file content (SOUL,
// AGENTS, plugin code) is scanned for key shapes (long words and hashes may appear in it).
import { z } from "zod";
import { AgentProfileSpecReportStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { findSecretLiterals, hasSecretShape, SafeLine } from "./agent-spec";

export const AGENT_SPEC_V2_FORMAT = "companyos.agent-spec/v2";
export const AGENT_SPEC_V2_BUNDLE_FORMAT = "companyos.agent-spec-bundle/v2";
/** A field CompanyOS has no source for yet. */
export const UNKNOWN = "unknown" as const;
const Unknown = z.literal(UNKNOWN);
const orUnknown = <T extends z.ZodType>(s: T) => z.union([s, Unknown]);

const Slug = (max: number) =>
  z
    .string()
    .max(max)
    .regex(new RegExp(`^[a-z0-9][a-z0-9._-]{0,${max - 1}}$`), "lowercase letters, digits and -_.");
export const ContainerName = Slug(128);
export const ProfileName = Slug(64);
const Name = z
  .string()
  .max(128)
  .regex(/^[A-Za-z0-9_][A-Za-z0-9._:-]{0,127}$/, "a name");
const Names = z.array(Name).max(500);
const Sha256 = z.string().max(64).regex(/^[0-9a-f]{64}$/, "64 lowercase hex characters");
const Commit = z.string().regex(/^[0-9a-f]{7,40}$/);
const RelPath = z
  .string()
  .min(1)
  .max(300)
  .refine((p) => !p.startsWith("/") && !p.split("/").includes(".."), "a relative path");

/** Where a credential is used and where its value lives (never the value). */
export const ProfileKeyRef = z
  .strictObject({
    /** The config path that holds the credential (`mcp_servers.recall.headers.Authorization`). */
    used_by: z
      .string()
      .max(200)
      .regex(/^[A-Za-z0-9_.-]{1,200}$/),
    /**
     * `litellm`: the gateway key; `recall`: the DE's Recall agent key; `companyos_agent_key`: the
     * DE's CompanyOS agent key; `container`: only in the container's own `.env` / config today.
     */
    source: z.enum(["litellm", "recall", "companyos_agent_key", "container"]),
    /** The env var that carries it; "unknown": the live value is a literal ClawPanel redacted. */
    env: orUnknown(
      z
        .string()
        .max(64)
        .regex(/^[A-Z][A-Z0-9_]{1,63}$/),
    ),
  })
  .meta({ id: "AgentProfileKeyRef" });

export const ProfileMcpServer = z
  .strictObject({
    name: Name,
    /** An http(s) URL or `${ENV}…` (null: a stdio server). */
    url: z
      .string()
      .max(500)
      .regex(/^(?:https?:\/\/|\$\{[A-Z][A-Z0-9_]{1,63}\})[^\s@]*$/)
      .nullable(),
    /** A stdio server's command (null: an http server). */
    command: z.string().max(300).nullable(),
    /** The header NAMES the server is sent (values are never here; credentials are in `keys`). */
    header_names: z.array(z.string().max(64)).max(16),
    tools: z.strictObject({
      /** `tools.include`: only these register; null: every tool the server offers. */
      include: Names.nullable(),
      exclude: Names.nullable(),
      resources: z.boolean().nullable(),
      prompts: z.boolean().nullable(),
    }),
  })
  .meta({ id: "AgentProfileMcpServer" });

/** The CompanyOS MCP server's tool list: configured, served (tool profiles), and effective. */
export const ProfileCompanyosTools = z
  .strictObject({
    /** The profile's `mcp_servers.companyos.tools.include` (null: no include list, every tool). */
    configured_include: Names.nullable(),
    /** The tool profile the server serves this caller (`services/api/src/mcp/tool-profiles.ts`). */
    tool_profile: orUnknown(z.string().max(64)).nullable(),
    /** `settings.flags.mcp_tool_profiles` / `tool_lookup` of the company. */
    flags: orUnknown(z.strictObject({ mcp_tool_profiles: z.boolean(), tool_lookup: z.boolean() })),
    /** The company's `settings.mcp_tool_profiles.<profile>` add / remove. */
    company_setting: orUnknown(
      z.strictObject({ add: Names, remove: Names, core: Names.nullable() }),
    ),
    /** What `tools/list` serves: the code default after the company's add/remove ("all": no profile). */
    served: orUnknown(z.union([Names, z.literal("all")])),
    /** What the agent registers: `configured_include` ∩ `served` ("all": every tool). */
    effective: orUnknown(z.union([Names, z.literal("all")])),
  })
  .meta({ id: "AgentProfileCompanyosTools" });

export const ProfileSkill = z
  .strictObject({
    name: Name,
    category: Name.nullable(),
    /** `root`: the container's shared `/opt/data/skills`; `profile`: the profile's own. */
    scope: z.enum(["root", "profile"]),
    /** The frontmatter `version` ("unknown": the skill declares none, live neither). */
    version: orUnknown(z.string().min(1).max(64)),
    /**
     * SHA-256 over exactly `files` (`path\0sha256\n` lines, sorted): the files the spec ships. A
     * live folder may hold more (in-place edit backups, agent-written notes); they do not count.
     */
    sha256: Sha256,
    files: z.array(z.strictObject({ path: RelPath, sha256: Sha256 })).max(500),
    /**
     * The pin that governs it, if any: `platform-skills` (the panel's base-rules bytes, installed
     * on a full baseline apply) or `hub-lock` (Hermes' skills/.hub/lock.json). Null: not pinned.
     */
    pin: z
      .strictObject({
        source: z.enum(["platform-skills", "hub-lock"]),
        version: z.string().max(64).nullable(),
        sha256: Sha256.nullable(),
        note: z.string().max(200).nullable(),
      })
      .nullable(),
  })
  .meta({ id: "AgentProfileSkill" });

/** One file the spec ships, with its content (UTF-8) and the SHA-256 of its bytes. */
export const SpecFile = z
  .strictObject({
    /** Relative to the plugin's folder. */
    path: RelPath,
    sha256: Sha256,
    bytes: z.int().min(0).max(1_000_000),
    content: z.string().max(1_000_000),
  })
  .meta({ id: "AgentSpecFile" });

/** Where a plugin's files come from: a git tree at a commit. */
export const SpecPluginSource = z
  .strictObject({
    repo: z.enum(["CompanyOS", "clawpanel-dashboard"]),
    commit: Commit,
    path: RelPath,
    /** Why this commit (the branch or deploy manifest that names it). */
    ref: z.string().min(1).max(300),
  })
  .meta({ id: "AgentSpecPluginSource" });

export const ProfilePlugin = z
  .strictObject({
    name: Name,
    /** The installed version (plugin.yaml; "unknown": no source for it). */
    version: orUnknown(z.string().min(1).max(64)),
    /** The first 12 hex of the installed `__init__.py`'s SHA-256 (null: not known). */
    init_sha12: z
      .string()
      .regex(/^[0-9a-f]{12}$/)
      .nullable(),
    /** The install folder under plugins/ when spelled differently from the name (kept as is). */
    folder: z.string().max(64).nullable(),
    /** First 12 hex of other files' SHA-256 (e.g. `adapter.py`), when known. */
    files_sha12: z.record(z.string().max(64), z.string().regex(/^[0-9a-f]{12}$/)).nullable(),
    /** `unverified`: enabled in config but not found installed under its name (being checked). */
    status: z.enum(["verified", "unverified"]),
    /** Where its files come from (null: no source in any repo). */
    source: SpecPluginSource.nullable(),
    /** Every file the plugin ships (tests excluded). Null: not managed, leave the installed files. */
    files: z.array(SpecFile).max(200).nullable(),
  })
  .meta({ id: "AgentProfilePlugin" });

/** SOUL.md or AGENTS.md: the whole file, its hash, and the block the panel owns inside it. */
export const SpecDocFile = z
  .strictObject({
    content: z.string().max(500_000),
    /** SHA-256 of `content`'s UTF-8 bytes. */
    sha256: Sha256,
    bytes: z.int().min(0).max(2_000_000),
    /**
     * A block ClawPanel writes inside the file (the identity block): `keep_live` — the applier
     * keeps the container's own block between the markers and writes the rest. Null: no block.
     */
    panel_block: z
      .strictObject({
        start: z.string().min(1).max(100),
        end: z.string().min(1).max(100),
        policy: z.literal("keep_live"),
      })
      .nullable(),
    /**
     * SHA-256 of `content` with the panel block's lines removed: from the start marker through the
     * end marker, markers included, plus the newline after the end marker (as
     * tools/agent-spec/extract-htva.py `doc_file`). Compare the live file the same way. Null: no
     * panel block.
     */
    sha256_outside_panel_block: Sha256.nullable(),
    /**
     * The `companyos:platform` block (company, seat, supervisor, the seat's boards by id), which
     * CompanyOS writes with `platformBlock` (domain-agents identity/platform-block.ts).
     * `rendered`: `content` holds it rendered from the seat's LIVE data when this was served —
     * write it as is. `keep_live`: there was no live data to render from (an offline dump, a
     * profile with no seat): keep the container's own block between the markers. Null: no block.
     */
    platform_block: z
      .strictObject({
        start: z.literal("<!-- companyos:platform:start -->"),
        end: z.literal("<!-- companyos:platform:end -->"),
        policy: z.enum(["rendered", "keep_live"]),
      })
      .nullable(),
  })
  .meta({ id: "AgentSpecDocFile" });

const ToolsetName = z
  .string()
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);

export const SpecV2Model = z
  .strictObject({
    default: z.string().min(1).max(200),
    provider: z.string().max(64).nullable(),
    effort: z.enum(["low", "medium", "high"]).nullable(),
    vision: z.boolean(),
    context_length: z.int().min(1).nullable(),
    /** `auxiliary.<task>.model` (vision, compression, …); null: Hermes' defaults. */
    auxiliary: z.record(z.string().max(64), z.string().max(200)).nullable(),
    prompt_caching: z.boolean(),
  })
  .meta({ id: "AgentSpecV2Model" });

export const SpecV2Approvals = z
  .strictObject({
    mode: z.string().max(32).nullable(),
    unattended_mode: z.string().max(32).nullable(),
    cron_mode: z.string().max(32).nullable(),
    deny: z.array(z.string().min(1).max(200)).max(200),
  })
  .meta({ id: "AgentSpecV2Approvals" });

const docShape = {
  format: z.literal(AGENT_SPEC_V2_FORMAT),
  container: ContainerName,
  profile: ProfileName,
  /** `de`: the profile is a DE (it names its membership); `helper`: an AG with no DE. */
  kind: z.enum(["de", "helper"]),
  de_membership_id: Uuid.nullable(),
  model: SpecV2Model.nullable(),
  toolsets: z
    .strictObject({
      /** `agent.disabled_toolsets`. */
      disabled: z.array(ToolsetName).max(64),
      /** `platform_toolsets.<platform>` (null: Hermes' default per platform). */
      platforms: z.record(z.string().max(32), z.array(ToolsetName).max(64)).nullable(),
    })
    .nullable(),
  mcp: z
    .strictObject({
      servers: z.array(ProfileMcpServer).max(16),
      /** Null: the profile has no CompanyOS MCP server. */
      companyos: ProfileCompanyosTools.nullable(),
    })
    .nullable(),
  skills: z
    .strictObject({
      installed: z.array(ProfileSkill).max(500),
      /** `skills.disabled`. */
      disabled: Names,
      /** The `.no-bundled-skills` marker (Hermes' bundled skills are not installed). */
      no_bundled_skills: z.boolean(),
      write_approval: z.boolean().nullable(),
      /** Hermes-bundled skills found in the canonical folder: Hermes' own, not shipped by the spec. */
      bundled_excluded: Names,
    })
    .nullable(),
  plugins: z
    .strictObject({
      enabled: z.array(ProfilePlugin).max(64),
      disabled: Names,
      /** The terminal-allow plugin's mode (null: not configured). */
      terminal_allow: z.strictObject({ mode: z.string().min(1).max(32) }).nullable(),
    })
    .nullable(),
  /** The approvals block (null: not managed — the profile's canonical config has none). */
  approvals: SpecV2Approvals.nullable(),
  recall: z
    .strictObject({
      /** `memory.provider` (null: none, as every helper). */
      memory_provider: z.string().max(64).nullable(),
      /** `memory.provider: recall` (helpers: off). */
      enabled: z.boolean(),
      /** The Recall MCP tools the profile registers (empty: no Recall server). */
      tools: Names,
    })
    .nullable(),
  keys: z.array(ProfileKeyRef).max(32).nullable(),
  /** Files that are chattr +i live: the applier unlocks (chattr -i) and relocks (+i) them. */
  immutable_paths: z
    .strictObject({
      /** Every +i path of this profile (a container's own files are its root profile's). */
      paths: z.array(z.string().max(300)).max(100),
      /** Where the list comes from (an `lsattr` of every home). */
      source: z.string().max(300),
    })
    .nullable(),
  /** The profile's SOUL.md. */
  soul: SpecDocFile.nullable(),
  /** The profile's AGENTS.md. */
  agents: SpecDocFile.nullable(),
  provenance: z.strictObject({
    source: z.string().max(200),
    commit: Commit.nullable(),
    files: z.array(z.string().max(300)).max(50),
    /** The live facts merged in (plugin versions, pins, env names, +i paths). */
    facts: z.string().max(600),
  }),
  /** Every field above that is "unknown", as a dotted path (sorted). */
  unknown_fields: z.array(z.string().max(200)).max(500),
};

/** A hash or commit field holds hex by contract: not a secret. */
const HEX_PATH = /(?:^|[._])(?:sha256|sha256_outside_panel_block|commit)$/;
const HEX = /^[0-9a-f]{7,64}$/;
/** File content (prose and code): scanned for key shapes only. */
const CONTENT_PATH =
  /(?:^|\.)(?:soul|agents)\.content$|(?:^|\.)plugins\.enabled\.\d+\.files\.\d+\.content$/;

/** Paths of secret-shaped strings in a v2 document (hash fields excluded). */
export function profileSpecSecretHits(doc: unknown): string[] {
  const hits: string[] = [];
  const walk = (v: unknown, path: string) => {
    if (typeof v === "string") {
      const at = path || "body";
      if (HEX_PATH.test(path) && HEX.test(v)) return;
      const rk = /\brk_[A-Za-z0-9_-]{16,}/.test(v);
      if (CONTENT_PATH.test(path)) {
        // Code may say "[redacted]" itself; the export's placeholder is what must never appear.
        if (rk || hasSecretShape(v) || /\[REDACTED by ClawPanel/i.test(v)) hits.push(at);
      } else if (rk || /\[REDACTED/i.test(v) || findSecretLiterals(v).length > 0) hits.push(at);
    } else if (Array.isArray(v)) {
      for (const [i, x] of v.entries()) walk(x, `${path}.${i}`.replace(/^\./, ""));
    } else if (v && typeof v === "object")
      for (const [k, x] of Object.entries(v)) {
        if (hasSecretShape(k)) hits.push(`${path}.${k}`.replace(/^\./, ""));
        walk(x, `${path}.${k}`.replace(/^\./, ""));
      }
  };
  walk(doc, "");
  return [...new Set(hits)].sort();
}

export const AgentSpecV2Doc = z
  .strictObject(docShape)
  .superRefine((d, ctx) => {
    for (const p of profileSpecSecretHits(d).slice(0, 10))
      ctx.addIssue({
        code: "custom",
        path: p.split(".").filter(Boolean),
        message: "looks like a secret value: a spec holds key references only",
      });
  })
  .meta({ id: "AgentSpecV2Doc" });
export type AgentSpecV2Doc = z.infer<typeof AgentSpecV2Doc>;

// --- the container's signed bundle ---------------------------------------------------------------

export const AgentSpecV2BundleProfile = z
  .object({
    profile: z.string(),
    de_membership_id: Uuid.nullable(),
    /** The profile's spec version CompanyOS wants (null: no spec, not managed). */
    desired_version: z.int().min(1).nullable(),
    /** SHA-256 of the canonical JSON of `spec` (report it back with `desired_version`). */
    spec_sha256: Sha256.nullable(),
    /** The same hash with every tool list as a set: equal means no config change. */
    compare_sha256: Sha256.nullable(),
    /** Null: CompanyOS holds no spec for this profile — the applier leaves it alone. */
    spec: AgentSpecV2Doc.nullable(),
  })
  .meta({ id: "AgentSpecV2BundleProfile" });

/** The signed payload (decode `payload`, verify, then parse as this). */
export const AgentSpecV2Bundle = z
  .object({
    format: z.literal(AGENT_SPEC_V2_BUNDLE_FORMAT),
    container_id: Uuid,
    company_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string(),
    /** The container's bundle version: bumped whenever the content changes. */
    version: z.int().min(1),
    /** SHA-256 of the canonical JSON of the payload without `version`, `content_sha256`, `issued_at`. */
    content_sha256: Sha256,
    issued_at: Timestamp,
    profiles: z.array(AgentSpecV2BundleProfile),
  })
  .meta({ id: "AgentSpecV2Bundle" });
export type AgentSpecV2Bundle = z.infer<typeof AgentSpecV2Bundle>;

export const AgentSpecV2SignedBundle = z
  .object({
    format: z.literal(AGENT_SPEC_V2_BUNDLE_FORMAT),
    alg: z.literal("Ed25519"),
    key_id: z.string(),
    /** base64url of the exact bytes signed: canonical JSON of an `AgentSpecV2Bundle`. */
    payload: z.string(),
    /** base64url Ed25519 signature over the payload bytes. */
    signature: z.string(),
    /** Copies of the payload's version and content hash (unsigned; use the payload's). */
    version: z.int().min(1),
    content_sha256: z.string(),
  })
  .meta({ id: "AgentSpecV2SignedBundle" });
export type AgentSpecV2SignedBundle = z.infer<typeof AgentSpecV2SignedBundle>;

// --- the applier's report -------------------------------------------------------------------------

export const AgentSpecV2Report = z
  .strictObject({
    /** The profile spec version the report is about (must be the desired one). */
    version: z.int().min(1),
    /** That version's `spec_sha256`. */
    spec_sha256: Sha256,
    status: AgentProfileSpecReportStatus,
    /** One short line (a reason class); never a body or a key. */
    reason: SafeLine.optional(),
    details: z
      .strictObject({
        /** How the change took effect (`none`, `reload`, `restart`). */
        reload: z.string().trim().min(1).max(32).optional(),
        /** Files written (or that would be, for `shadow_planned`), with hashes before and after. */
        files_changed: z
          .array(
            z.strictObject({
              path: z.string().min(1).max(300),
              before: Sha256.nullable(),
              after: Sha256.nullable(),
            }),
          )
          .max(500)
          .optional(),
        /** Where the pre-apply backup is (a path on the node). */
        backup: z.string().trim().min(1).max(300).optional(),
        duration_ms: z.int().min(0).max(86_400_000).optional(),
      })
      .optional(),
  })
  .meta({ id: "AgentSpecV2Report" });

export const AgentSpecV2ReportAck = z
  .object({
    ok: z.literal(true),
    desired_version: z.int(),
    applied_version: z.int().nullable(),
    last_status: AgentProfileSpecReportStatus,
    /** The profile runs the version CompanyOS wants. */
    in_sync: z.boolean(),
  })
  .meta({ id: "AgentSpecV2ReportAck" });
